import { useNavigate, useParams } from "react-router-dom";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetVehicleDetailQuery } from "../../service/features/api";
import { CircularProgress } from "@mui/material";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import moment from "moment";
import { PAYMENT_MODE, SELLER_TYPE, VEHICLE_CONDITION } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";
import {SaudiRiyal} from "lucide-react";

const ManageVehicleDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: vehicleResponse, isLoading, isError } = useGetVehicleDetailQuery(id);

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </div>
    );
  }

  if (isError || !vehicleResponse?.data) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3>Error loading vehicle details</h3>
        <button className="btn btn_primary" onClick={() => navigate("/manage-vehicles")}>Back to List</button>
      </div>
    );
  }

  const vehicle = vehicleResponse.data;

  // Helper to get enum labels
  // (Removed local getVehicleTypeLabel as it's now imported)

  const getPaymentMethodLabel = (method: number) => {
    switch (method) {
      case PAYMENT_MODE.CASH: return "Cash";
      case PAYMENT_MODE.ONLINE: return "Online";
      // case PAYMENT_MODE.CARD: return "Card";
      default: return "N/A";
    }
  };

  const getSellerTypeLabel = (type: number) => {
    switch (type) {
      case SELLER_TYPE.PERSONAL: return "Personal";
      case SELLER_TYPE.BUSINESS: return "Business";
      default: return "N/A";
    }
  };

  const getVehicleConditionLabel = (condition: number) => {
    switch (condition) {
      case VEHICLE_CONDITION.BRAND_NEW: return "Brand New";
      case VEHICLE_CONDITION.LIKE_NEW: return "Like New";
      case VEHICLE_CONDITION.EXCELLENT: return "Excellent";
      case VEHICLE_CONDITION.GOOD: return "Good";
      case VEHICLE_CONDITION.FAIR: return "Fair";
      case VEHICLE_CONDITION.POOR: return "Poor";
      case VEHICLE_CONDITION.DAMAGED: return "Damaged";
      case VEHICLE_CONDITION.NON_RUNNING: return "Non Running";
      case VEHICLE_CONDITION.FOR_PARTS: return "For Parts";
      case VEHICLE_CONDITION.RESTORED: return "Restored";
      case VEHICLE_CONDITION.CLASSIC: return "Classic";
      default: return "N/A";
    }
  };

  const typeLabel = getVehicleTypeLabel(vehicle.type);

  const listedInfo = [
    { label: "Ad Title", value: vehicle.name },
    {
      label: "Price",
      value: <strong className="c_primary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><SaudiRiyal size={16} />{vehicle.price?.toLocaleString() || "0"}</strong>,
    },
    { label: "Category", value: vehicle.categoryId?.name || "N/A" },
    { label: "Sub Category", value: vehicle.modelId?.name || "N/A" },
    { label: "Location", value: `${vehicle.address?.line1 || ""}, ${vehicle.address?.city || ""}, ${vehicle.address?.state || ""} ${vehicle.address?.zip || ""}` },
    { label: "Registration No", value: vehicle.registrationNo || "N/A" },
    {
      label: !!vehicle.isElectric ? "Range" : "Mileage",
      value: `${vehicle.milage || vehicle.mileage || 0} ${vehicle.miles ? "Miles" : "KM"}`
    },
    { label: "Seller Type", value: getSellerTypeLabel(vehicle.sellerType) },
    { label: "Payment Method", value: getPaymentMethodLabel(vehicle.paymentMethod) },
    { label: "Condition", value: getVehicleConditionLabel(vehicle.condition) },
    { label: "Description", value: vehicle.description || "N/A" },
    {
      label: "Featured Vehicle",
      value: <SwitchToggle checked={vehicle.isFeatured} />,
    },
    {
      label: "Luxury Vehicle",
      value: <SwitchToggle checked={vehicle.isLuxury} />,
    },
    {
      label: "YouTube Video",
      value: vehicle.videoUrl || "N/A"
    },
    { label: "Created Date", value: moment(vehicle.createdAt).format("DD-MMM-YYYY") },
  ];

  const technicalData = [
    { label: "Brand", value: vehicle.brandId?.name || "N/A" },
    { label: "Model", value: vehicle.modelId?.name || "N/A" },
    { label: "Variant", value: vehicle.variantId?.name || "N/A" },
    { label: "First Registration Year", value: vehicle.registrationYear || "N/A" },
    { label: "Year of manufacturing", value: vehicle.manufactureYear || "N/A" },
    { label: "Seats", value: vehicle.seats || "N/A" },
    { label: "Transmission", value: vehicle.transmissionId?.name || "N/A" },
    { label: "Drive Type", value: vehicle.driveTypeId?.name || "N/A" },
    { label: "Axles", value: vehicle.axlesId?.name || "N/A" },
    { label: "Engine Power", value: vehicle.engineDetailId?.name || "N/A" },
    { label: "Length", value: vehicle.length ? `${vehicle.length} mm` : "N/A" },
    { label: "Width", value: vehicle.width ? `${vehicle.width} mm` : "N/A" },
    { label: "Height", value: vehicle.height ? `${vehicle.height} mm` : "N/A" },
    { label: "Weight", value: vehicle.weight ? `${vehicle.weight} kg` : "N/A" },
    { label: "GCC Spec", value: vehicle.gcc ? "Yes" : "No" },
    { label: "HU Valid Until", value: vehicle.huValid ? moment(vehicle.huValid).format("DD-MMM-YYYY") : "N/A" },
    { label: "Electric", value: vehicle.isElectric ? "Yes" : "No" },
    { label: "Battery Capacity", value: vehicle.isElectric && vehicle.batteryCapacity ? `${vehicle.batteryCapacity} Wh` : "N/A" },
    { label: "Damage Type", value: vehicle.damageTypeId?.name || "No Damage" },
    { label: "Available", value: vehicle.isAvailable ? "Yes" : "No" },
  ];

  const colorData = [
    {
      label: "Interior Color",
      value: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {vehicle.interiorColorId && (
            <span
              style={{
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                backgroundColor: vehicle.interiorColorId.colorCode || vehicle.interiorColorId.name,
                border: "1px solid #ccc",
              }}
            />
          )}
          <span>{vehicle.interiorColorId?.name || "N/A"}</span>
        </div>
      )
    },
    {
      label: "Exterior Color",
      value: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {vehicle.exteriorColorId && (
            <span
              style={{
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                backgroundColor: vehicle.exteriorColorId.colorCode || vehicle.exteriorColorId.name,
                border: "1px solid #ccc",
              }}
            />
          )}
          <span>{vehicle.exteriorColorId?.name || "N/A"}</span>
        </div>
      )
    },
  ];

  const dealerInfo = [
    {
      label: "Dealer Name",
      value: (
        <strong
          className="anchor_link"
          onClick={() => navigate(`/manage-dealers/detail?id=${vehicle.dealerId?._id}`)}
        >
          {vehicle.dealerId?.fullName || "N/A"}
        </strong>
      ),
    },
    { label: "Phone Number", value: vehicle.contactInfo?.phone || "N/A" },
    { label: "Email", value: vehicle.contactInfo?.email || "N/A" },
  ];

  return (
    <>
      {/* Page Title */}
      <CommonBreadcrumb
        title={`${typeLabel} Detail`}
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Listed Vehicles", onClick: () => navigate("/manage-vehicles") },
          { label: `${typeLabel} Detail` },
        ]}
      />

      {/* ================= Card 1: Listed Vehicles Info ================= */}
      <div className="cards view_page">
        <div className="table_header mb_20">
          <h2 className="flex">Listed Information <span className="status_btn c_dark_primary">{typeLabel}</span></h2>
          <div className="btn_flex">
            <span className={`status_btn dark ${vehicle.isActive ? "c_success" : "c_danger"}`}>
              {vehicle.isActive ? "Active" : "Inactive"}
            </span>
            {vehicle.condition?.name && (
              <span className="status_btn c_dark_info">{vehicle.condition.name}</span>
            )}
          </div>
        </div>

        <div className="gap_p">
          {/* Images */}
          <div className="w_100">
            <h6>Listed Vehicles Images</h6>
            <div className="gap_p">
              {vehicle.images && vehicle.images.length > 0 ? (
                vehicle.images.map((img: string, index: number) => (
                  <div className="w_25" key={index}>
                    <div className="vew_img no_radius v2">
                      <figure>
                        <img src={img} className="view_img" alt={`Vehicle image ${index + 1}`} onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }} />
                      </figure>
                    </div>
                  </div>
                ))
              ) : (
                <div className="w_25">
                  <div className="vew_img no_radius v2">
                    <figure>
                      <img src="/static/images/placeholder.png" className="view_img" alt="Placeholder" />
                    </figure>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Listed Vehicles Details */}
          {listedInfo.map((item, index) => (
            <div
              className={item.label.includes("Description") || item.label.includes("Location") ? "w_100" : "w_33"}
              key={index}
            >
              <h6>{item.label}</h6>
              <p>{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="cards view_page mt_2">
        <div className="table_header mb_20">
          <h2>Technical Data</h2>
        </div>
        <div className="gap_p">
          {technicalData.map((item, index) => (
            <div className="w_33" key={index}>
              <h6>{item.label}</h6>
              <p>{item.value}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="cards view_page mt_2">
        <div className="table_header mb_20">
          <h2>Vehicle Colors</h2>
        </div>
        <div className="gap_p">
          {colorData.map((item, index) => (
            <div className="w_33" key={index}>
              <h6>{item.label}</h6>
              <p>{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {vehicle.features && vehicle.features.length > 0 && (
        <div className="cards view_page mt_2">
          <div className="table_header mb_20">
            <h2>Features</h2>
          </div>
          <div className="gap_p gap_sm">
            {vehicle.features.map((feature: any, index: number) => (
              <div className="w_33 flex_items" key={index}>
                <span className="c_primary">✔</span>
                <span>{feature.name || feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="cards view_page mt_2">
        <div className="table_header mb_20">
          <h2>Dealer Information</h2>
        </div>
        <div className="gap_p">
          {dealerInfo.map((item, index) => (
            <div className="w_33" key={index}>
              <h6>{item.label}</h6>
              <p>{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ManageVehicleDetail;
